import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { getDisplayName } from '../../utils';
import {
  appointmentDraftActions,
  appointmentDraftHelpers,
  appointmentDraftApi,
  journeyStateActions,
  journeyStateApi,
  tokenApi,
} from '~/api';
import { getReduxDraftFromDecodedToken } from '~/api/appointment/appointment-draft';
import {
  getTokenFromStorage,
  restoreOriginalTokenFromTemporary,
} from '../persist-token/persist-token.utils';
import { apiCallFinished } from '../loading-spinner';
import { isEditAppointmentPage } from '~/routes/helpers';

export const withRestorableDraft = (Component) =>
  class extends PureComponent {
    state = {
      isMergeCompleted: false,
    };

    static displayName = `WithRestorableDraft(${getDisplayName(Component)})`;

    static propTypes = {
      dispatchApiCallFinished: PropTypes.func.isRequired,
      dispatchSetDraftAppointment: PropTypes.func.isRequired,
      dispatchSetJourneyState: PropTypes.func.isRequired,
      draft: PropTypes.shape({}).isRequired,
      journeyState: PropTypes.shape({}).isRequired,
      router: PropTypes.shape({
        action: PropTypes.string.isRequired,
      }).isRequired,
      editMode: PropTypes.bool,
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
      }).isRequired,
    };

    static defaultProps = {
      editMode: false,
    };

    componentDidMount() {
      const {
        dispatchApiCallFinished,
        draft,
        router,
        dispatchSetDraftAppointment,
        dispatchSetJourneyState,
        editMode,
        location: { pathname },
        journeyState,
      } = this.props;

      if (!editMode || !isEditAppointmentPage(pathname)) {
        restoreOriginalTokenFromTemporary();
      }

      if (router.action === 'POP') {
        dispatchApiCallFinished(false);
        const tokenFromStorage = getTokenFromStorage();

        const tokenPromise = tokenFromStorage
          ? Promise.resolve(tokenFromStorage)
          : tokenApi.getInitialToken();

        tokenPromise.then((token) => {
          appointmentDraftApi
            .getDecodedAppointmentDraft(token)
            .then((decodedToken) => {
              const reduxDraftFromToken = getReduxDraftFromDecodedToken(decodedToken);
              const mergedReduxDraft = appointmentDraftHelpers.mergeReduxDrafts(
                reduxDraftFromToken,
                draft,
              );

              dispatchSetDraftAppointment(mergedReduxDraft);
            })
            .then(() => journeyStateApi.getDecodedJourneyState(token))
            .then((decodedToken) => {
              const mergedReduxJourneyState = appointmentDraftHelpers.mergeReduxDrafts(
                decodedToken,
                journeyState,
              );
              dispatchSetJourneyState(mergedReduxJourneyState);
            })
            .then(() => {
              dispatchApiCallFinished(true);
              this.setState({ isMergeCompleted: true });
            });
        });
      } else {
        this.setState({ isMergeCompleted: true });
      }
    }

    render() {
      const { isMergeCompleted } = this.state;

      if (isMergeCompleted) {
        return <Component {...this.props} />;
      }
      return null;
    }
  };

export const mapStateToProps = ({
  appointment: { draft },
  journeyState,
  editAppointment: { editMode },
  router,
}) => ({
  draft,
  router,
  editMode,
  journeyState,
});

const mapDispatchToProps = {
  dispatchApiCallFinished: apiCallFinished,
  dispatchSaveAppointmentDraft: appointmentDraftActions.saveAppointmentDraft,
  dispatchSetDraftAppointment: appointmentDraftActions.setDraftAppointment,
  dispatchSaveJourneyState: journeyStateActions.saveJourneyState,
  dispatchSetJourneyState: journeyStateActions.setJourneyState,
};

export const ConnectedWithRestorableDraft = (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(withRestorableDraft(Component));
